import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Shield, RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Search, Filter, Mail, Globe, Clock, Play, Loader, ChevronRight
} from 'lucide-react';

const STATUS_CONFIG = {
  active:           { label: 'Active',     icon: CheckCircle, cls: 'text-emerald-600 bg-emerald-50', dot: 'bg-emerald-500' },
  erreur_auth:      { label: 'Erreur auth', icon: XCircle,    cls: 'text-red-600 bg-red-50',        dot: 'bg-red-500' },
  erreur_connexion: { label: 'Erreur connexion', icon: AlertTriangle, cls: 'text-amber-600 bg-amber-50', dot: 'bg-amber-500' },
  pending:          { label: 'En attente', icon: Clock,       cls: 'text-gray-500 bg-gray-50',      dot: 'bg-gray-400' },
  inconnu:          { label: 'Non vérifié', icon: Clock,      cls: 'text-gray-500 bg-gray-50',      dot: 'bg-gray-400' },
  inactive:         { label: 'Inactive',   icon: XCircle,     cls: 'text-gray-400 bg-gray-50',      dot: 'bg-gray-300' },
};

export default function MonitoringEmail() {
  const navigate = useNavigate();
  const [data, setData] = useState({ stats: {}, clients: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [testingId, setTestingId] = useState(null);
  const [testingAll, setTestingAll] = useState(false);
  const [refreshingDomains, setRefreshingDomains] = useState(false);
  const [renewingDomain, setRenewingDomain] = useState(null);

  const fetchData = useCallback(() => {
    api.get('/monitoring/emails').then(r => setData(r.data)).catch(() => toast.error("Erreur chargement")).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const testSingle = async (clientId) => {
    setTestingId(clientId);
    try {
      const r = await api.post(`/monitoring/test-email/${clientId}`);
      toast.success(`${r.data.email}: ${r.data.status === 'ok' ? 'OK' : r.data.status} (${r.data.message_count} messages)`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur test");
    } finally { setTestingId(null); }
  };

  const testAll = async () => {
    setTestingAll(true);
    try {
      const r = await api.post('/monitoring/test-all-emails');
      toast.success(`Test terminé: ${r.data.results.ok} OK, ${r.data.results.erreur_auth + r.data.results.erreur_connexion} erreurs sur ${r.data.tested}`);
      fetchData();
    } catch (e) {
      toast.error("Erreur test global");
    } finally { setTestingAll(false); }
  };

  const refreshDomains = async () => {
    setRefreshingDomains(true);
    try {
      const r = await api.post('/monitoring/refresh-domains');
      toast.success(`${r.data.results.success} domaines vérifiés, ${r.data.results.expiring_30j} expirent dans 30j`);
      fetchData();
    } catch (e) {
      toast.error("Erreur vérification OVH");
    } finally { setRefreshingDomains(false); }
  };

  const renewDomain = async (domain) => {
    setRenewingDomain(domain);
    try {
      await api.post(`/monitoring/domain-renew/${domain}`);
      toast.success(`Renouvellement auto activé pour ${domain}`);
      fetchData();
    } catch (e) {
      toast.error(`Erreur renouvellement: ${e.response?.data?.detail || "erreur"}`);
    } finally { setRenewingDomain(null); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '—'; }
  };

  const daysUntil = (d) => {
    if (!d) return null;
    try { return Math.ceil((new Date(d) - new Date()) / 86400000); }
    catch { return null; }
  };

  const filtered = data.clients.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!(c.societe?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.domain?.toLowerCase().includes(q))) return false;
    }
    if (statusFilter && c.status !== statusFilter) return false;
    return true;
  });

  const { stats } = data;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="monitoring-email-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring Email</h1>
          <p className="text-sm text-gray-500 mt-1">Supervision des boîtes mail et domaines clients</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={testAll}
            disabled={testingAll}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
            data-testid="test-all-btn"
          >
            {testingAll ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
            {testingAll ? 'Test en cours...' : 'Tester toutes les boîtes'}
          </button>
          <button
            onClick={refreshDomains}
            disabled={refreshingDomains}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
            data-testid="refresh-domains-btn"
          >
            {refreshingDomains ? <Loader size={14} className="animate-spin" /> : <Globe size={14} />}
            {refreshingDomains ? 'Vérification...' : 'Vérifier domaines OVH'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total || 0, cls: 'text-gray-900' },
          { label: 'Actives', value: stats.actives || 0, cls: 'text-emerald-600' },
          { label: 'Erreurs', value: stats.erreurs || 0, cls: stats.erreurs ? 'text-red-600 font-bold' : 'text-gray-400' },
          { label: 'Sans mot de passe', value: stats.sans_mdp || 0, cls: stats.sans_mdp ? 'text-amber-600' : 'text-gray-400' },
          { label: 'Domaines < 30j', value: stats.domains_expiring_30j || 0, cls: stats.domains_expiring_30j ? 'text-red-600 font-bold' : 'text-gray-400' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-[10px] font-medium text-gray-500 uppercase">{s.label}</p>
            <p className={`text-xl font-bold mt-0.5 ${s.cls}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher client, email, domaine..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gray-900/10 outline-none"
            data-testid="search-monitoring"
          />
        </div>
        <div className="relative">
          <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm bg-white appearance-none cursor-pointer outline-none"
            data-testid="filter-status-monitoring"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="monitoring-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Dernier check</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Domaine</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Expiration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    <Shield size={32} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Aucune boîte trouvée</p>
                  </td>
                </tr>
              ) : filtered.map(c => {
                const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.inconnu;
                const Icon = cfg.icon;
                const days = daysUntil(c.domain_expiration);
                const expirationCls = days === null ? 'text-gray-400' : days <= 7 ? 'text-red-600 font-bold' : days <= 30 ? 'text-amber-600 font-medium' : 'text-gray-600';

                return (
                  <tr key={c.client_id} className="hover:bg-gray-50 transition-colors" data-testid={`monitoring-row-${c.client_id}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/clients/${c.client_id}`)}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                      >
                        {c.societe}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 font-mono">{c.email}</span>
                      {!c.has_password && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">MDP manquant</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.cls}`}>
                        <Icon size={12} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{formatDate(c.last_check)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{c.domain}</span>
                      {c.domain_renew_auto && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium">Auto</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${expirationCls}`}>
                        {days !== null ? (days <= 0 ? 'Expiré !' : `${days}j — ${formatDate(c.domain_expiration).split(' ')[0]}`) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); testSingle(c.client_id); }}
                          disabled={testingId === c.client_id}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 disabled:opacity-50 transition-colors"
                          title="Tester la connexion"
                          data-testid={`test-btn-${c.client_id}`}
                        >
                          {testingId === c.client_id ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
                        </button>
                        {c.domain && !c.domain_renew_auto && (
                          <button
                            onClick={(e) => { e.stopPropagation(); renewDomain(c.domain); }}
                            disabled={renewingDomain === c.domain}
                            className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-500 hover:text-emerald-700 disabled:opacity-50 transition-colors"
                            title="Activer renouvellement auto"
                            data-testid={`renew-btn-${c.domain}`}
                          >
                            {renewingDomain === c.domain ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">{filtered.length} boîte{filtered.length > 1 ? 's' : ''} sur {data.clients.length}</p>
        </div>
      </div>
    </div>
  );
}
